/**
 * SimpleIngestOutlineEditor.jsx - Gate 3 Outline Editor
 * 
 * CRITICAL: This is part of the ISOLATED Simple Ingestion pipeline.
 * It replicates the proven UX of OutlineViewerModalV3 but works with
 * local files instead of production APIs.
 * 
 * Features:
 * - View/Edit segment text
 * - Click-to-split with search
 * - Reorder segments
 * - Rename segments
 * - Delete segments
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  ChevronUp, 
  ChevronDown, 
  Eye, 
  Trash2, 
  Scissors,
  Save,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Check,
  AlertTriangle
} from 'lucide-react';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

export default function SimpleIngestOutlineEditor({ 
  isOpen, 
  onClose, 
  jobId,  // Changed from sessionId to jobId
  onComplete,
  apiBaseUrl = null  // Optional: Custom API base URL for publisher-managed authors
}) {
  // Use custom API base or default simple-ingest
  const getApiBase = () => apiBaseUrl || `${API}/simple-ingest`;
  
  // Core Data State
  const [outline, setOutline] = useState(null);
  const [editedSegments, setEditedSegments] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingText, setSavingText] = useState(false);
  
  // Get auth token
  const getToken = () => localStorage.getItem('accessToken');
  
  // Text Editing State
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedText, setEditedText] = useState('');
  
  // Click-to-Split State
  const [isClickToSplitMode, setIsClickToSplitMode] = useState(false);
  const [splitPoints, setSplitPoints] = useState([]);
  const [pendingSplitIndex, setPendingSplitIndex] = useState(null);
  const [tempSplitLabel, setTempSplitLabel] = useState('');
  const [adjustingSplitIndex, setAdjustingSplitIndex] = useState(null);
  const [showFirstSegmentPrompt, setShowFirstSegmentPrompt] = useState(false);
  const [firstSegmentLabel, setFirstSegmentLabel] = useState('');
  const [splitting, setSplitting] = useState(false);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  
  // Status State
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Refs
  const textRef = useRef(null);
  const searchHighlightRefs = useRef([]);

  // ==========================================================================
  // DATA FETCHING (JOB-BASED)
  // ==========================================================================
  
  const fetchOutline = useCallback(async () => {
    if (!jobId) {
      console.log('[SimpleIngest] Gate 3: No jobId provided');
      return;
    }
    
    console.log('[SimpleIngest] Gate 3: Fetching outline for job:', jobId);
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBase()}/jobs/${jobId}/gate-3/outline`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      // Check response status BEFORE reading body
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
        } catch {
          // If JSON parsing fails, use the HTTP status message
        }
        console.error('[SimpleIngest] Gate 3: Failed to load outline:', errorMessage);
        alert('Failed to load outline: ' + errorMessage);
        setLoading(false);
        return;
      }
      
      // For success responses, read the body (job-based returns wrapped response)
      const result = await response.json();
      const data = result.data || result;  // Handle both wrapped and unwrapped responses
      console.log('[SimpleIngest] Gate 3: Loaded outline with', data.segments?.length || 0, 'segments');
      
      setOutline(data);
      // Filter out deleted segments for editing
      const activeSegments = (data.segments || []).filter(s => !s.is_deleted);
      setEditedSegments(JSON.parse(JSON.stringify(activeSegments)));
      
    } catch (error) {
      console.error('[SimpleIngest] Gate 3: Error loading outline:', error);
      alert('Error loading outline: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchSegmentText = useCallback(async (segmentId) => {
    if (!jobId) return;
    
    console.log('[SimpleIngest] Gate 3: Fetching segment text:', segmentId);
    setSegmentLoading(true);
    
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[SimpleIngest] Gate 3: Fetch attempt ${attempt}/${maxRetries} for segment:`, segmentId);
        
        const response = await fetch(`${getApiBase()}/jobs/${jobId}/gate-3/segment/${segmentId}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        
        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;  // Handle both wrapped and unwrapped
          console.log('[SimpleIngest] Gate 3: Loaded segment with', data.word_count, 'words');
          setSelectedSegment(data);
          setSegmentLoading(false);
          return; // Success - exit retry loop
        }
        
        // Non-OK response - check if retryable
        if (response.status === 404 && attempt < maxRetries) {
          console.warn(`[SimpleIngest] Gate 3: 404 on attempt ${attempt}, retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue; // Retry
        }
        
        // Non-retryable error or max retries reached
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
        } catch {
          // Use HTTP status
        }
        console.error('[SimpleIngest] Gate 3: Failed to load segment:', errorMessage);
        alert('Failed to load segment: ' + errorMessage);
        setSegmentLoading(false);
        return;
        
      } catch (error) {
        if (attempt < maxRetries) {
          console.warn(`[SimpleIngest] Gate 3: Network error on attempt ${attempt}, retrying...`, error.message);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue; // Retry on network errors
        }
        alert('Error loading segment: ' + error.message);
        setSegmentLoading(false);
        return;
      }
    }
  }, [jobId]);

  // Load outline on mount
  useEffect(() => {
    if (isOpen && jobId) {
      fetchOutline();
      setHasUnsavedChanges(false);
      setSaveSuccess(false);
    }
  }, [isOpen, jobId, fetchOutline]);

  // ==========================================================================
  // TEXT EDITING
  // ==========================================================================
  
  const handleStartEditingText = () => {
    setIsEditingText(true);
    setEditedText(selectedSegment.full_text);
  };

  const handleCancelEditingText = () => {
    setIsEditingText(false);
    setEditedText('');
  };

  const handleSaveSegmentText = async () => {
    if (!selectedSegment || !jobId) return;
    
    setSavingText(true);
    try {
      const response = await fetch(
        `${getApiBase()}/jobs/${jobId}/gate-3/segment/${selectedSegment.id}`,
        {
          method: 'PATCH',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({ full_text: editedText })
        }
      );
      
      const result = await response.json();
      const data = result.data || result;  // Handle wrapped response
      
      if (response.ok && (result.ok !== false)) {
        // Update in segments list
        setEditedSegments(prev => prev.map(seg =>
          seg.id === selectedSegment.id
            ? { ...seg, word_count: data.word_count, character_count: data.new_length || editedText.length }
            : seg
        ));
        
        setIsEditingText(false);
        setEditedText('');
        
        // Return to outline list after successful save
        setSelectedSegment(null);
        
        // Show success briefly (will show in outline view)
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert('Failed to save text: ' + (result.message || data.detail || data.error));
      }
    } catch (error) {
      alert('Error saving text: ' + error.message);
    } finally {
      setSavingText(false);
    }
  };

  // ==========================================================================
  // CLICK-TO-SPLIT
  // ==========================================================================
  
  const handleEnterClickToSplitMode = () => {
    setIsClickToSplitMode(true);
    setSplitPoints([]);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
    setPendingSplitIndex(null);
    setAdjustingSplitIndex(null);
  };

  const handleExitClickToSplitMode = () => {
    setIsClickToSplitMode(false);
    setSplitPoints([]);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
    setPendingSplitIndex(null);
    setAdjustingSplitIndex(null);
  };

  // Search in text
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query || !selectedSegment?.full_text) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }
    
    const text = selectedSegment.full_text.toLowerCase();
    const searchLower = query.toLowerCase();
    const results = [];
    
    let index = text.indexOf(searchLower);
    while (index !== -1) {
      results.push({ charIndex: index, length: query.length });
      index = text.indexOf(searchLower, index + 1);
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    
    // Scroll to first result
    if (results.length > 0) {
      setTimeout(() => scrollToSearchResult(0), 100);
    }
  }, [selectedSegment?.full_text]);

  const scrollToSearchResult = (index) => {
    const highlight = searchHighlightRefs.current[index];
    if (highlight) {
      highlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSearchNavigation = (direction) => {
    if (searchResults.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    }
    
    setCurrentSearchIndex(newIndex);
    setTimeout(() => scrollToSearchResult(newIndex), 100);
  };

  // Handle click in text to place split
  const handleTextClick = (e) => {
    if (!isClickToSplitMode || adjustingSplitIndex !== null || pendingSplitIndex !== null) return;
    if (!selectedSegment?.full_text) return;
    
    const text = selectedSegment.full_text;
    
    // Get selection
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    // Calculate character index by walking the text nodes
    let charIndex = 0;
    const walker = document.createTreeWalker(
      textRef.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node;
    let found = false;
    
    while ((node = walker.nextNode())) {
      // Skip text inside split markers
      const isMarkerText = node.parentElement?.closest('[data-split-marker="true"]');
      if (isMarkerText) continue;
      
      if (node === range.startContainer) {
        charIndex += range.startOffset;
        found = true;
        break;
      }
      charIndex += node.textContent.length;
    }
    
    if (!found || charIndex <= 0 || charIndex >= text.length - 1) {
      return;
    }
    
    // Snap to word boundary
    charIndex = findWordBoundary(text, charIndex);
    
    // Check if too close to existing split
    const tooClose = splitPoints.some(sp => Math.abs(sp.charIndex - charIndex) < 20);
    if (tooClose) {
      alert('Split point too close to existing split');
      return;
    }
    
    // Set pending split
    setPendingSplitIndex(charIndex);
    setTempSplitLabel(`Chapter ${splitPoints.length + 2}`);
  };

  // Find word boundary
  const findWordBoundary = (text, index) => {
    // Move backward to start of word
    while (index > 0 && /\S/.test(text[index - 1])) {
      index--;
    }
    // Skip whitespace forward
    while (index < text.length && /\s/.test(text[index])) {
      index++;
    }
    return index;
  };

  // Confirm split point
  const handleConfirmSplit = () => {
    if (pendingSplitIndex === null || !tempSplitLabel.trim()) {
      alert('Please enter a label for this segment');
      return;
    }
    
    const newSplit = {
      charIndex: pendingSplitIndex,
      label: tempSplitLabel.trim()
    };
    
    setSplitPoints(prev => [...prev, newSplit].sort((a, b) => a.charIndex - b.charIndex));
    setPendingSplitIndex(null);
    setTempSplitLabel('');
  };

  const handleCancelPendingSplit = () => {
    setPendingSplitIndex(null);
    setTempSplitLabel('');
  };

  const handleRemoveSplit = (index) => {
    setSplitPoints(prev => prev.filter((_, i) => i !== index));
    setAdjustingSplitIndex(null);
  };

  const handleToggleAdjustSplit = (index) => {
    setAdjustingSplitIndex(adjustingSplitIndex === index ? null : index);
  };

  // Adjust split position
  const handleAdjustSplit = (index, direction, boundary) => {
    const split = splitPoints[index];
    const text = selectedSegment.full_text;
    let newIndex = split.charIndex;
    
    if (boundary === 'word') {
      if (direction === 'left') {
        // Move to previous word
        newIndex--;
        while (newIndex > 0 && /\s/.test(text[newIndex])) newIndex--;
        while (newIndex > 0 && /\S/.test(text[newIndex - 1])) newIndex--;
      } else {
        // Move to next word
        while (newIndex < text.length && /\S/.test(text[newIndex])) newIndex++;
        while (newIndex < text.length && /\s/.test(text[newIndex])) newIndex++;
      }
    } else if (boundary === 'sentence') {
      if (direction === 'left') {
        // Move to previous sentence
        newIndex = text.lastIndexOf('.', newIndex - 2);
        if (newIndex !== -1) {
          newIndex += 2;
          // Skip whitespace
          while (newIndex < text.length && /\s/.test(text[newIndex])) newIndex++;
        } else {
          newIndex = 0;
        }
      } else {
        // Move to next sentence
        newIndex = text.indexOf('.', newIndex);
        if (newIndex !== -1) {
          newIndex += 2;
          // Skip whitespace
          while (newIndex < text.length && /\s/.test(text[newIndex])) newIndex++;
        } else {
          newIndex = text.length;
        }
      }
    }
    
    // Clamp
    newIndex = Math.max(1, Math.min(text.length - 1, newIndex));
    
    // Check for collision
    const collision = splitPoints.some((sp, i) => 
      i !== index && Math.abs(sp.charIndex - newIndex) < 20
    );
    if (collision) {
      alert('Too close to another split point');
      return;
    }
    
    setSplitPoints(prev => prev.map((sp, i) =>
      i === index ? { ...sp, charIndex: newIndex } : sp
    ).sort((a, b) => a.charIndex - b.charIndex));
    
    // Update adjusting index to new position
    const newSortedIndex = [...splitPoints].sort((a, b) => a.charIndex - b.charIndex)
      .findIndex(sp => sp.charIndex === newIndex || (index === splitPoints.indexOf(sp)));
    setAdjustingSplitIndex(splitPoints.findIndex(sp => 
      sp.charIndex === splitPoints[index]?.charIndex
    ));
  };

  // Show first segment prompt
  const handleShowFirstSegmentPrompt = () => {
    if (splitPoints.length === 0) {
      alert('No split points defined');
      return;
    }
    setShowFirstSegmentPrompt(true);
    setFirstSegmentLabel(selectedSegment?.label || 'Introduction');
  };

  // Apply custom splits
  const handleApplyCustomSplits = async () => {
    if (!firstSegmentLabel.trim()) {
      alert('Please provide a label for the first segment');
      return;
    }
    
    setSplitting(true);
    
    try {
      const labels = [firstSegmentLabel.trim(), ...splitPoints.map(sp => sp.label)];
      const charIndices = splitPoints.map(sp => sp.charIndex);
      
      const response = await fetch(
        `${getApiBase()}/jobs/${jobId}/gate-3/segment/${selectedSegment.id}/split`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            split_positions: charIndices,
            first_segment_label: firstSegmentLabel.trim(),
            new_segment_labels: splitPoints.map(sp => sp.label)
          })
        }
      );
      
      const result = await response.json();
      const data = result.data || result;
      
      if (response.ok && (result.ok !== false)) {
        setShowFirstSegmentPrompt(false);
        setIsClickToSplitMode(false);

        // In-place update from authoritative backend response
        // The split endpoint now returns the full segment list post-write
        const responseSegments = data.segments;

        if (responseSegments && responseSegments.length > 0) {
          // Use authoritative segment list from backend (same shape as GET outline)
          const activeSegments = responseSegments.filter(s => !s.is_deleted);
          setEditedSegments(activeSegments);
        } else {
          // Fallback: reconstruct locally from split response IDs + local text
          const newSegmentIds = data.new_segment_ids || [];
          const fullText = selectedSegment?.full_text || '';
          const sortedSplits = [...splitPoints].sort((a, b) => a.charIndex - b.charIndex);

          if (newSegmentIds.length > 0) {
            const reconstructedSegments = [];
            let prevIndex = 0;

            for (let i = 0; i < newSegmentIds.length; i++) {
              const segId = newSegmentIds[i];
              const endIndex = i < sortedSplits.length ? sortedSplits[i].charIndex : fullText.length;
              const segText = fullText.slice(prevIndex, endIndex).trim();
              const segLabel = i === 0 ? firstSegmentLabel.trim() : sortedSplits[i - 1]?.label || `Part ${i + 1}`;

              reconstructedSegments.push({
                id: segId,
                label: segLabel,
                title: segLabel,
                custom_label: segLabel,
                word_count: segText.split(/\s+/).filter(w => w).length,
                character_count: segText.length,
                charCount: segText.length,
                wordCount: segText.split(/\s+/).filter(w => w).length,
                is_deleted: false
              });

              prevIndex = endIndex;
            }

            setEditedSegments(prev => {
              const idx = prev.findIndex(s => s.id === selectedSegment.id);
              if (idx === -1) return prev;
              const updated = [...prev];
              updated.splice(idx, 1, ...reconstructedSegments);
              return updated;
            });
          }
        }

        setSplitPoints([]);
        setSelectedSegment(null); // Return to outline list
        
        alert(`Successfully split into ${data.new_segment_count || newSegmentIds.length || 'multiple'} segments`);
      } else {
        alert('Failed to split: ' + (result.message || data.detail || data.error));
      }
    } catch (error) {
      alert('Error splitting segment: ' + error.message);
    } finally {
      setSplitting(false);
    }
  };

  // ==========================================================================
  // OUTLINE MANAGEMENT
  // ==========================================================================
  
  const handleLabelChange = (segmentId, newLabel) => {
    setEditedSegments(prev => prev.map(seg =>
      seg.id === segmentId ? { ...seg, label: newLabel } : seg
    ));
    setHasUnsavedChanges(true);
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!confirm('Are you sure you want to remove this segment? It will be excluded from the final manuscript.')) {
      return;
    }
    
    try {
      const response = await fetch(
        `${getApiBase()}/jobs/${jobId}/gate-3/segment/${segmentId}`,
        { 
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );
      
      const result = await response.json();
      const data = result.data || result;
      
      if (response.ok && (result.ok !== false)) {
        setEditedSegments(prev => prev.filter(seg => seg.id !== segmentId));
        setHasUnsavedChanges(true);
      } else {
        alert('Failed to delete: ' + (result.message || data.detail || data.error));
      }
    } catch (error) {
      alert('Error deleting segment: ' + error.message);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    
    const newSegments = [...editedSegments];
    [newSegments[index - 1], newSegments[index]] = [newSegments[index], newSegments[index - 1]];
    
    // Update order
    newSegments.forEach((seg, idx) => {
      seg.order = idx + 1;
    });
    
    setEditedSegments(newSegments);
    setHasUnsavedChanges(true);
  };

  const handleMoveDown = (index) => {
    if (index === editedSegments.length - 1) return;
    
    const newSegments = [...editedSegments];
    [newSegments[index], newSegments[index + 1]] = [newSegments[index + 1], newSegments[index]];
    
    // Update order
    newSegments.forEach((seg, idx) => {
      seg.order = idx + 1;
    });
    
    setEditedSegments(newSegments);
    setHasUnsavedChanges(true);
  };

  const handleSaveOutline = async () => {
    setSaving(true);
    
    try {
      const updates = editedSegments.map(seg => ({
        id: seg.id,
        order: seg.order,
        label: seg.label || seg.custom_label || seg.title,
        is_deleted: seg.is_deleted !== undefined ? seg.is_deleted : false
      }));
      
      const response = await fetch(
        `${getApiBase()}/jobs/${jobId}/gate-3/outline`,
        {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({ segments: updates })
        }
      );
      
      const result = await response.json();
      const data = result.data || result;
      
      if (response.ok && (result.ok !== false)) {
        setHasUnsavedChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert('Failed to save outline: ' + (result.message || data.detail || data.error));
      }
    } catch (error) {
      alert('Error saving outline: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (hasUnsavedChanges) {
      await handleSaveOutline();
    }
    if (onComplete) {
      // Only call onComplete - DO NOT call onClose
      // The parent (StreamlinedUploadFlow) will handle closing/advancing
      onComplete();
    } else {
      // Only call onClose if there's no onComplete callback
      // This is for standalone usage (not in StreamlinedUploadFlow)
      onClose();
    }
  };

  // ==========================================================================
  // RENDER TEXT WITH SPLIT MARKERS
  // ==========================================================================
  
  const renderTextWithSplitMarkers = () => {
    if (!selectedSegment?.full_text) return null;
    
    const text = selectedSegment.full_text;
    const elements = [];
    let lastIndex = 0;
    
    // Sort split points
    const sortedSplits = [...splitPoints].sort((a, b) => a.charIndex - b.charIndex);
    
    // Build markers list
    const markers = [];
    
    // Add split markers
    sortedSplits.forEach((sp, i) => {
      markers.push({ type: 'split', index: sp.charIndex, splitIndex: i, label: sp.label });
    });
    
    // Add search highlights
    searchResults.forEach((sr, i) => {
      markers.push({ 
        type: 'search', 
        index: sr.charIndex, 
        length: sr.length, 
        searchIndex: i,
        isCurrent: i === currentSearchIndex
      });
    });
    
    // Add pending split
    if (pendingSplitIndex !== null) {
      markers.push({ type: 'pending', index: pendingSplitIndex });
    }
    
    // Sort all markers by position
    markers.sort((a, b) => a.index - b.index);
    
    // Render
    markers.forEach((marker, markerIdx) => {
      // Add text before marker
      if (marker.index > lastIndex) {
        elements.push(
          <span key={`text-${markerIdx}`}>
            {text.substring(lastIndex, marker.index)}
          </span>
        );
        lastIndex = marker.index;
      }
      
      if (marker.type === 'split') {
        const isAdjusting = adjustingSplitIndex === marker.splitIndex;
        
        elements.push(
          <span
            key={`split-${marker.splitIndex}`}
            data-split-marker="true"
            className={`inline-flex items-center gap-1 mx-1 px-2 py-1 rounded text-xs font-medium ${
              isAdjusting ? 'bg-orange-500' : 'bg-cyan-500'
            } text-white cursor-pointer`}
            onClick={(e) => { e.stopPropagation(); handleToggleAdjustSplit(marker.splitIndex); }}
          >
            <Scissors className="w-3 h-3" />
            <span>{marker.label}</span>
            {isAdjusting && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.splitIndex, 'left', 'sentence'); }}
                  className="hover:bg-orange-600 p-0.5 rounded"
                  title="Previous sentence"
                >
                  <ChevronsLeft className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.splitIndex, 'left', 'word'); }}
                  className="hover:bg-orange-600 p-0.5 rounded"
                  title="Previous word"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.splitIndex, 'right', 'word'); }}
                  className="hover:bg-orange-600 p-0.5 rounded"
                  title="Next word"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.splitIndex, 'right', 'sentence'); }}
                  className="hover:bg-orange-600 p-0.5 rounded"
                  title="Next sentence"
                >
                  <ChevronsRight className="w-3 h-3" />
                </button>
              </>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleRemoveSplit(marker.splitIndex); }}
              className="hover:bg-red-600 p-0.5 rounded ml-1"
              title="Remove split"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      } else if (marker.type === 'search') {
        elements.push(
          <mark
            key={`search-${marker.searchIndex}`}
            ref={el => searchHighlightRefs.current[marker.searchIndex] = el}
            className={marker.isCurrent ? 'bg-yellow-400 text-black font-bold' : 'bg-yellow-200 text-black'}
          >
            {text.substring(marker.index, marker.index + marker.length)}
          </mark>
        );
        lastIndex = marker.index + marker.length;
        return; // Don't update lastIndex again below
      } else if (marker.type === 'pending') {
        elements.push(
          <span key="pending" className="inline-block w-1 h-6 bg-red-500 animate-pulse mx-0.5" />
        );
      }
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(<span key="text-end">{text.substring(lastIndex)}</span>);
    }
    
    return elements;
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  // Calculate stats
  const activeSegments = editedSegments;
  const totalWords = activeSegments.reduce((sum, s) => sum + (s.word_count || 0), 0);
  const totalChars = activeSegments.reduce((sum, s) => sum + (s.character_count || 0), 0);

  return (
    <>
      {/* Main Outline Modal */}
      <Dialog open={isOpen && !selectedSegment} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              📖 Gate 3: Edit Outline
              {outline && (
                <div className="text-sm font-normal text-slate-400 mt-1">
                  &quot;{outline.title}&quot; {outline.author && `by ${outline.author}`}
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-2">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">{activeSegments.length}</div>
                  <div className="text-sm text-slate-400">Segments</div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">{totalWords.toLocaleString()}</div>
                  <div className="text-sm text-slate-400">Total Words</div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">{totalChars.toLocaleString()}</div>
                  <div className="text-sm text-slate-400">Characters</div>
                </div>
              </div>
              
              {/* Warning Banner */}
              {hasUnsavedChanges && (
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 mb-4 flex items-center gap-2">
                  <Save className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-300 text-sm">Changes pending. Save outline to finalize changes.</span>
                </div>
              )}
              
              {/* Large Segment Warning */}
              {activeSegments.some(s => s.word_count > 10000) && (
                <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-amber-300 font-medium text-sm">Large Segment Detected</p>
                      <p className="text-amber-400/80 text-xs mt-1">
                        Consider using &quot;Click to Split&quot; to break large segments into smaller chunks.
                      </p>
                      <ul className="text-amber-400/70 text-xs mt-1">
                        {activeSegments.filter(s => s.word_count > 10000).map(s => (
                          <li key={s.id}>• {s.label}: {s.word_count?.toLocaleString()} words</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Segments List */}
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-300 text-sm">Segments (Editable)</h3>
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  {activeSegments.map((segment, index) => (
                    <div
                      key={segment.id}
                      className="border-b border-slate-700/50 last:border-b-0 p-3 hover:bg-slate-800/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5 items-center">
                          <button
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            className="p-1 hover:bg-slate-700 rounded disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-white"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <span className="text-xs text-slate-500 font-mono">{segment.order}</span>
                          <button
                            onClick={() => handleMoveDown(index)}
                            disabled={index === activeSegments.length - 1}
                            className="p-1 hover:bg-slate-700 rounded disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-white"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Label input */}
                        <div className="flex-1 min-w-0">
                          <Input
                            value={segment.label}
                            onChange={(e) => handleLabelChange(segment.id, e.target.value)}
                            className="font-medium bg-slate-800 border-slate-600 text-white"
                          />
                          {segment.label !== segment.original_label && (
                            <div className="text-xs text-slate-500 mt-1 truncate">
                              Original: &quot;{segment.original_label}&quot;
                            </div>
                          )}
                        </div>
                        
                        {/* Stats */}
                        <div className="flex items-center gap-2 text-xs text-slate-400 whitespace-nowrap">
                          <span className="bg-slate-700 px-2 py-1 rounded">{segment.type}</span>
                          <span>{segment.word_count?.toLocaleString() || 0} words</span>
                          <span>{segment.character_count?.toLocaleString() || 0} chars</span>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/30"
                            onClick={() => fetchSegmentText(segment.id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                            onClick={() => handleDeleteSegment(segment.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t border-slate-700 pt-4 mt-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm">
                {saveSuccess && (
                  <span className="text-emerald-400 flex items-center gap-1">
                    <Check className="w-4 h-4" /> Saved successfully
                  </span>
                )}
                {hasUnsavedChanges && !saveSuccess && (
                  <span className="text-amber-400">● Unsaved changes</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Close without saving
                </Button>
                <Button
                  onClick={handleSaveOutline}
                  disabled={saving || !hasUnsavedChanges}
                  className="bg-slate-700 hover:bg-slate-600"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSaveAndContinue}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  Save and Continue →
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Segment Viewer Modal */}
      <Dialog open={!!selectedSegment} onOpenChange={() => setSelectedSegment(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedSegment?.label}</span>
              <span className="text-sm font-normal text-slate-400">
                {selectedSegment?.word_count?.toLocaleString() || 0} words · {selectedSegment?.character_count?.toLocaleString() || 0} characters
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {segmentLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {/* Edit Mode */}
              {isEditingText && (
                <>
                  <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3 mb-4 flex-shrink-0">
                    <span className="text-amber-300 text-sm">
                      ⚠️ Editing this text will be saved to the local segment file.
                    </span>
                  </div>
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="flex-1 w-full p-4 bg-slate-950 border border-slate-700 rounded-lg font-mono text-sm text-slate-300 resize-none min-h-96"
                  />
                </>
              )}
              
              {/* Click-to-Split Mode */}
              {isClickToSplitMode && !isEditingText && (
                <>
                  <div className="bg-cyan-900/30 border border-cyan-700 rounded-lg p-3 mb-4 flex-shrink-0 flex items-center gap-2">
                    <Scissors className="w-4 h-4 text-cyan-400" />
                    <span className="text-cyan-300 text-sm">
                      <strong>Click-to-Split Mode:</strong> Use search to find chapter breaks, then click in the text to add split points.
                    </span>
                  </div>
                  
                  {/* Search Bar */}
                  <div className="flex gap-2 mb-4 flex-shrink-0">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search for 'Chapter', 'Part', or any text..."
                        className="pl-10 bg-slate-800 border-slate-600 text-white"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchNavigation('next')}
                      />
                    </div>
                    {searchResults.length > 0 && (
                      <>
                        <span className="flex items-center text-sm text-slate-400 whitespace-nowrap">
                          {currentSearchIndex + 1} of {searchResults.length}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSearchNavigation('prev')}
                          className="border-slate-600"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSearchNavigation('next')}
                          className="border-slate-600"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {searchQuery && searchResults.length === 0 && (
                      <span className="flex items-center text-sm text-red-400">No matches</span>
                    )}
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-slate-400 mb-3 flex-shrink-0">
                    <span><strong className="text-white">{selectedSegment?.character_count?.toLocaleString()}</strong> characters</span>
                    <span><strong className="text-cyan-400">{splitPoints.length}</strong> split points</span>
                    <span><strong className="text-emerald-400">{splitPoints.length + 1}</strong> segments after split</span>
                  </div>
                  
                  {/* Pending split input */}
                  {pendingSplitIndex !== null && (
                    <div className="bg-cyan-900/50 border-2 border-cyan-500 rounded-lg p-4 mb-3 flex-shrink-0">
                      <div className="text-sm text-cyan-300 mb-2">
                        <strong>Split at character {pendingSplitIndex.toLocaleString()}</strong>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          value={tempSplitLabel}
                          onChange={(e) => setTempSplitLabel(e.target.value)}
                          placeholder="Enter segment name (e.g., Chapter 2)..."
                          className="flex-1 bg-slate-800 border-slate-600 text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleConfirmSplit();
                            if (e.key === 'Escape') handleCancelPendingSplit();
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={handleConfirmSplit}
                          className="bg-emerald-600 hover:bg-emerald-500"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Add Split
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelPendingSplit}
                          className="border-slate-600"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Text with markers */}
                  <div
                    ref={textRef}
                    onClick={handleTextClick}
                    className="flex-1 overflow-y-auto border border-slate-700 rounded-lg p-4 bg-slate-950 cursor-text select-text"
                    style={{ minHeight: '300px' }}
                  >
                    <div 
                      className="whitespace-pre-wrap leading-relaxed font-serif text-slate-300"
                      style={{ fontSize: '15px', lineHeight: '1.8' }}
                    >
                      {renderTextWithSplitMarkers()}
                    </div>
                  </div>
                  
                  {/* Split points summary */}
                  {splitPoints.length > 0 && (
                    <div className="mt-3 p-3 bg-slate-800/50 border border-slate-700 rounded-lg flex-shrink-0">
                      <div className="text-sm font-medium text-slate-300 mb-2">Splits ({splitPoints.length}):</div>
                      <div className="flex flex-wrap gap-2">
                        {splitPoints.map((split, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-600 text-white text-xs rounded cursor-pointer hover:bg-red-600 transition-colors"
                            onClick={() => handleRemoveSplit(index)}
                            title="Click to remove"
                          >
                            {index + 2}. {split.label}
                            <X className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* View Mode */}
              {!isEditingText && !isClickToSplitMode && selectedSegment && (
                <>
                  {selectedSegment.manually_edited && (
                    <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 mb-4 flex-shrink-0">
                      <span className="text-blue-300 text-sm">ℹ️ This segment has been manually edited.</span>
                    </div>
                  )}
                  <div className="flex-1 overflow-y-auto border border-slate-700 rounded-lg p-4 bg-slate-950">
                    <pre className="whitespace-pre-wrap font-serif text-sm text-slate-300 leading-relaxed">
                      {selectedSegment.full_text}
                    </pre>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter className="border-t border-slate-700 pt-4 mt-4 flex-shrink-0">
            {isEditingText ? (
              <div className="flex gap-2 w-full justify-between">
                <Button
                  variant="outline"
                  onClick={() => setSelectedSegment(null)}
                  className="border-slate-600 text-slate-300"
                >
                  Back to Outline
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCancelEditingText}
                    className="border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveSegmentText}
                    disabled={savingText}
                    className="bg-indigo-600 hover:bg-indigo-500"
                  >
                    {savingText ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : isClickToSplitMode ? (
              <div className="flex gap-2 w-full justify-between">
                <Button
                  variant="outline"
                  onClick={() => setSelectedSegment(null)}
                  className="border-slate-600 text-slate-300"
                >
                  Back to Outline
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleExitClickToSplitMode}
                    className="border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleShowFirstSegmentPrompt}
                    disabled={splitPoints.length === 0 || splitting}
                    className="bg-cyan-600 hover:bg-cyan-500"
                  >
                    {splitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Splitting...
                      </>
                    ) : (
                      <>
                        <Scissors className="w-4 h-4 mr-2" />
                        Apply Splits ({splitPoints.length + 1} segments)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 w-full justify-between">
                <Button
                  variant="outline"
                  onClick={() => setSelectedSegment(null)}
                  className="border-slate-600 text-slate-300"
                >
                  Back to Outline
                </Button>
                <div className="flex gap-2">
                  <Button
                    onClick={handleStartEditingText}
                    className="bg-indigo-600 hover:bg-indigo-500"
                  >
                    Edit Text
                  </Button>
                  <Button
                    onClick={handleEnterClickToSplitMode}
                    className="bg-cyan-600 hover:bg-cyan-500"
                  >
                    <Scissors className="w-4 h-4 mr-1" />
                    Click to Split
                  </Button>
                </div>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* First Segment Label Prompt */}
      <Dialog open={showFirstSegmentPrompt} onOpenChange={() => setShowFirstSegmentPrompt(false)}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Name the First Segment</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 mb-4">
            Enter a name for the first segment (the content before your first split point):
          </p>
          <Input
            value={firstSegmentLabel}
            onChange={(e) => setFirstSegmentLabel(e.target.value)}
            placeholder="e.g., Introduction, Chapter 1..."
            className="bg-slate-800 border-slate-600 text-white"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && firstSegmentLabel.trim()) {
                handleApplyCustomSplits();
              }
              if (e.key === 'Escape') {
                setShowFirstSegmentPrompt(false);
              }
            }}
          />
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowFirstSegmentPrompt(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyCustomSplits}
              disabled={!firstSegmentLabel.trim() || splitting}
              className="bg-cyan-600 hover:bg-cyan-500"
            >
              {splitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                '✂️ Apply Splits'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
